import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import PageTitle from "@Components/PageTitle/PageTitle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import themeCreator from "@Themes/themeCreator";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import useGetAll from "@Hooks/useGetAll";
import { GET_MANAGER_DASHBOARD_DATA } from "@API/APIRoutes";
import { ListingPageContainer } from "../ListingPageTemplate/ListingPageTemplateStyles";
import { blue, teal, indigo, cyan, lightBlue } from "@mui/material/colors";

export default function ClearanceDashboard() {
  const theme = themeCreator();
  const [dashboardData, setDashBoardData] = useState({});
  const { loading, getAll } = useGetAll();

  useEffect(() => {
    getAll(GET_MANAGER_DASHBOARD_DATA, "No data available").then((response) => {
      setDashBoardData(response || {});
    });
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  const COLORS = [
    "#1A258A", // Deep Blue
    "#3A7CA5", // Light Blue
    "#5BC0EB", // Sky Blue
    "#F4A261", // Orange
    "#E76F51", // Red
    "#2A9D8F", // Green
    "#E9C46A", // Yellow
    "#A29BFE", // Lavender
    "#A0AEC0", // Cool Gray
    "#5E60CE", // Indigo
    "#9AE6B4", // Mint Green
  ];

  const LIGHT_COLORS = [
    "#1A258A", // Deep Blue
    "#3A7CA5", // Light Blue
    "#5BC0EB", // Sky Blue
    "#F4A261", // Orange
    "#E76F51", // Red
    "#2A9D8F", // Green
    "#E9C46A", // Yellow
    "#A29BFE", // Lavender
    "#A0AEC0", // Cool Gray
    "#5E60CE", // Indigo
    "#9AE6B4", // Mint Green
  ];

  const getColor = (index) => COLORS[index % COLORS.length];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const barChartData =
    dashboardData?.releasedOrdersPerEachMonthList?.length > 0
      ? months.map((month, idx) => ({
          month,
          orders: dashboardData.releasedOrdersPerEachMonthList[idx] ?? 0,
        }))
      : [];

  const pieData = [
    { name: "Pending Orders", value: dashboardData?.pendingOrders || 0 },
    { name: "Released Orders", value: dashboardData?.releasedOrders || 0 },
  ];

  const wayOfShippingPieData = Object.entries(
    dashboardData?.releasedOrdersPerWayOfShipping || {}
  ).map(([name, count]) => ({
    name,
    value: count,
  }));

  const PortsPieData = Object.entries(
    dashboardData?.releasedOrdersPerPorts || {}
  ).map(([name, count]) => ({
    name,
    value: count,
  }));

  // Helper to check if pie data is empty or all zero
  const isPieDataEmpty = (data) =>
    !data || data.length === 0 || data.every((d) => !d.value || d.value === 0);

  // Helper to check if bar data is empty
  const isBarDataEmpty = (data) => !Array.isArray(data) || data.length === 0;

  return (
    <ListingPageContainer className="listingPageContainer">
      <Container
        maxWidth="xl"
        sx={{
          pb: 5,
          px: 2,
          borderRadius: 4,
          boxShadow: 3,
          height: "100%",
        }}
      >
        <Box className="LabelButtonBox" mb={7}>
          <PageTitle
            title="Clearance Dashboard"
            PageIcon={<DashboardIcon color="white" fontSize="large" />}
            iconColor={theme.palette.primary.main}
          />
        </Box>

        {/* Charts Section */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            justifyContent: "center",
            mb: 10,
          }}
        >
          {/* Doughnut Chart */}
          <Box sx={{ flex: 1, minWidth: 300, height: 320 }}>
            <Typography
              variant="h6"
              textAlign="center"
              mb={1}
              sx={{ fontWeight: "bold" }}
            >
              Order Status Distribution
            </Typography>
            {isPieDataEmpty(pieData) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={60}
                    label
                  >
                    {pieData.map((_, index) => (
                      <Cell key={index} fill={getColor(index)} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Way of Shipping Pie Chart */}
          <Box sx={{ flex: 1, minWidth: 300, height: 320 }}>
            <Typography
              variant="h6"
              textAlign="center"
              mb={1}
              sx={{ fontWeight: "bold" }}
            >
              Way of Shipping Distribution ( Released )
            </Typography>
            {isPieDataEmpty(wayOfShippingPieData) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={wayOfShippingPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {wayOfShippingPieData.map((_, index) => (
                      <Cell key={index} fill={getColor(index)} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Ports Pie Chart */}
          <Box sx={{ flex: 1, minWidth: 300, height: 320 }}>
            <Typography
              variant="h6"
              textAlign="center"
              mb={1}
              sx={{ fontWeight: "bold" }}
            >
              Released Orders by Port
            </Typography>
            {isPieDataEmpty(PortsPieData) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={PortsPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {PortsPieData.map((_, index) => (
                      <Cell key={index} fill={getColor(index)} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>

        <hr />
        {/* Bar Charts Section */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 400, height: 500, mb: 4 }}>
            <Typography
              variant="h6"
              textAlign="center"
              mb={2}
              sx={{ fontWeight: "bold" }}
            >
              Average Lead Time by Category
            </Typography>
            {isBarDataEmpty(dashboardData?.avgLeadTimePerCategory) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={dashboardData?.avgLeadTimePerCategory}
                  margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="categoryName"
                    interval={0}
                    tick={{
                      angle: -35,
                      textAnchor: "end",
                      dy: 10,
                      fontSize: 12,
                    }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="averageLeadTime" name="Average Lead Time">
                    {dashboardData?.avgLeadTimePerCategory?.map(
                      (entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      )
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 400, height: 500, mb: 4 }}>
            <Typography
              variant="h6"
              textAlign="center"
              mb={2}
              sx={{ fontWeight: "bold" }}
            >
              Average Form 4 Lead Time by Category
            </Typography>
            {isBarDataEmpty(dashboardData?.avgForm4LeadTimePerCategory) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={dashboardData?.avgForm4LeadTimePerCategory}
                  margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="categoryName"
                    interval={0}
                    tick={{
                      angle: -35,
                      textAnchor: "end",
                      dy: 10,
                      fontSize: 12,
                    }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="averageLeadTime" name="Average Form4 Lead Time">
                    {dashboardData?.avgForm4LeadTimePerCategory?.map(
                      (entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      )
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>

        {/* Horizontal Divider */}
        <Box
          sx={{
            width: "100%",
            height: "2px",
            backgroundColor: "#ccc",
            my: 4,
            borderRadius: 2,
          }}
        />

        {/* Port and Agent Lead Time Charts - Same Row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            justifyContent: "center",
            mb: 6,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 400, height: 500, mb: 4 }}>
            <Typography
              variant="h6"
              textAlign="center"
              mb={2}
              sx={{ fontWeight: "bold" }}
            >
              Average Lead Time by Port
            </Typography>
            {isBarDataEmpty(dashboardData?.avgLeadTimePerPort) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={dashboardData?.avgLeadTimePerPort}
                  margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="portName"
                    interval={0}
                    tick={{
                      angle: -45,
                      textAnchor: "end",
                      dy: 10,
                      fontSize: 12,
                    }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="averageLeadTime" name="Average Lead Time">
                    {dashboardData?.avgLeadTimePerPort?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 400, height: 500, mb: 4 }}>
            <Typography
              variant="h6"
              textAlign="center"
              mb={2}
              sx={{ fontWeight: "bold" }}
            >
              Average Lead Time by Agent
            </Typography>
            {isBarDataEmpty(dashboardData?.avgLeadTimePerAgent) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={dashboardData?.avgLeadTimePerAgent}
                  margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="agentName"
                    interval={0}
                    tick={{
                      angle: -45,
                      textAnchor: "end",
                      dy: 10,
                      fontSize: 12,
                    }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="averageLeadTime" name="Average Lead Time">
                    {dashboardData?.avgLeadTimePerAgent?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>

        {/* Bottom Charts Section */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "2px",
              backgroundColor: "#ccc",
              fontWeight: "bold",
              my: 2,
              borderRadius: 2,
            }}
          />

          {/* Released Orders by Category Bar Chart */}
          <Box
            sx={{
              flex: 1,
              minWidth: 300,
              height: 530,
              mb: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              mb={2}
              sx={{ fontWeight: "bold" }}
            >
              Released Orders by Category
            </Typography>
            {isBarDataEmpty(dashboardData?.ordersPerCategory) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dashboardData?.ordersPerCategory}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="categoryName" type="category" interval={0} />
                  <Tooltip />
                  <Bar
                    dataKey="releasedOrderCount"
                    name="Released Orders"
                    sx={{ mb: 2 }}
                  >
                    {dashboardData?.ordersPerCategory?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Orders Per Month Bar Chart */}
          <Box
            sx={{
              flex: 1,
              minWidth: 400,
              height: 530,
              mb: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              mb={2}
              sx={{ fontWeight: "bold" }}
            >
              Orders Per Month
            </Typography>
            {isBarDataEmpty(barChartData) ? (
              <Typography textAlign="center" color="text.secondary" mt={8}>
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barChartData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="month" type="category" interval={0} />
                  <Tooltip />
                  <Bar dataKey="orders" name="Orders Per Month" sx={{ mb: 4 }}>
                    {barChartData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>

        <hr />

        {/* Table Section */}
        <Box
          sx={{
            minWidth: "100%",
            mx: "auto",
            mb: 3,
            px: 3,
            py: 2,
            borderRadius: 3,
            boxShadow: 6,
          }}
        >
          <Typography
            variant="h6"
            textAlign="center"
            gutterBottom
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Orders Per Supplier
          </Typography>
          {isBarDataEmpty(dashboardData?.ordersPerSupplier) ? (
            <Typography textAlign="center" color="text.secondary" mt={8}>
              No data available
            </Typography>
          ) : (
            <TableContainer component={Box}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background:"#A0AEC0",
                    }}
                  >
                    <TableCell
                      sx={{
                        color: "#000",
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        letterSpacing: 1,
                      }}
                    >
                      Supplier Name
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: "#000",
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        letterSpacing: 1,
                      }}
                    >
                      Released Orders
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(dashboardData.ordersPerSupplier || []).map((row, idx) => {
                    const bgColor = "#94caf1cb";
                    const textColor = "#000"; // Light backgrounds need dark text

                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: bgColor,
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                            color: textColor,
                          }}
                        >
                          {row.supplierName}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                            color: textColor,
                          }}
                        >
                          {row.releasedOrderCount}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Container>
    </ListingPageContainer>
  );
}
